/**
 * Public FAQ page (pub-1-1)
 * Route: /faq — no authentication required.
 * Content is fetched from GET /api/v1/public/faq and grouped by section tag.
 * Client-side search filters items in real-time.
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FaqItem {
  id: string;
  slug: string;
  title: string;
  content_md: string;
  tags: string[] | null;
}

// Section order and display labels
const SECTION_ORDER = [
  'general',
  'compliance',
  'signals',
  'broker',
  'pricing',
  'b2b',
  'technical',
] as const;

const SECTION_LABELS: Record<string, string> = {
  general: 'General',
  compliance: 'Compliance & FAIS',
  signals: 'Investment Banker & Signals',
  broker: 'Broker Integration',
  pricing: 'Subscriptions & Pricing',
  b2b: 'B2B Organisations',
  technical: 'Technical & Security',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

function getSectionFromTags(tags: string[] | null): string {
  if (!tags) return 'general';
  const sectionTag = tags.find((t) => t.startsWith('section:'));
  return sectionTag ? sectionTag.replace('section:', '') : 'general';
}

/**
 * Render inline rich text (bold + links) from a single text segment.
 * The segment has already been split on bold markers.
 */
function renderInlineParts(text: string, keyPrefix: string) {
  // Handle inline link [text](href)
  const linkMatch = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
  if (linkMatch) {
    const before = text.slice(0, text.indexOf(linkMatch[0]));
    const after = text.slice(text.indexOf(linkMatch[0]) + linkMatch[0].length);
    return (
      <span key={keyPrefix}>
        {before}
        <Link to={linkMatch[2]} className="text-teal-400 hover:text-teal-300 underline">
          {linkMatch[1]}
        </Link>
        {after}
      </span>
    );
  }
  return <span key={keyPrefix}>{text}</span>;
}

/** Render very basic markdown-like content (bold, newlines, links, bullets). */
function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5 text-slate-300 text-sm leading-relaxed">
      {lines.map((line, idx) => {
        if (line.trim() === '') return <br key={idx} />;

        // Bullet list item: line starting with "- "
        const isBullet = line.startsWith('- ');
        const lineContent = isBullet ? line.slice(2) : line;

        // Split on bold markers **...**
        const parts = lineContent.split(/(\*\*[^*]+\*\*)/g);
        const rendered = parts.map((part, pi) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={pi} className="text-white font-semibold">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return renderInlineParts(part, String(pi));
        });

        if (isBullet) {
          return (
            <div key={idx} className="flex gap-2 pl-2">
              <span className="text-teal-400 flex-shrink-0 mt-0.5">•</span>
              <span>{rendered}</span>
            </div>
          );
        }
        return <p key={idx}>{rendered}</p>;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accordion Item
// ---------------------------------------------------------------------------

interface AccordionItemProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ item, isOpen, onToggle }: AccordionItemProps) {
  return (
    <div
      className="border border-slate-700/50 rounded-lg overflow-hidden"
      id={item.slug}
    >
      <button
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 bg-slate-800/60 hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-white font-medium text-sm">{item.title}</span>
        {isOpen ? (
          <ChevronUp size={16} className="text-teal-400 flex-shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 py-4 bg-slate-900/60 border-t border-slate-700/30">
          <SimpleMarkdown text={item.content_md} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------

interface FaqSectionProps {
  sectionKey: string;
  items: FaqItem[];
  openItems: Set<string>;
  onToggle: (id: string) => void;
}

function FaqSection({ sectionKey, items, openItems, onToggle }: FaqSectionProps) {
  const label = SECTION_LABELS[sectionKey] ?? sectionKey;
  if (items.length === 0) return null;
  return (
    <section id={sectionKey} className="mb-8 scroll-mt-8">
      <h2 className="text-amber-400 font-bold text-base mb-3 flex items-center gap-2">
        <span className="w-1 h-5 bg-amber-400 rounded-full inline-block" />
        {label}
      </h2>
      <div className="space-y-2">
        {items.map((item) => (
          <AccordionItem
            key={item.id}
            item={item}
            isOpen={openItems.has(item.id)}
            onToggle={() => onToggle(item.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// JSON-LD FAQPage structured data
// ---------------------------------------------------------------------------

function FaqStructuredData({ items }: { items: FaqItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.content_md,
      },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ---------------------------------------------------------------------------
// Main FAQ page
// ---------------------------------------------------------------------------

export default function Faq() {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());


  useEffect(() => {
    fetch(`${API_URL}/api/v1/public/faq`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<FaqItem[]>;
      })
      .then((data) => setItems(data))
      .catch(() => setError('Could not load FAQ content. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.content_md.toLowerCase().includes(q),
    );
  }, [items, search]);

  const grouped = useMemo(() => {
    const map: Record<string, FaqItem[]> = {};
    for (const item of filteredItems) {
      const section = getSectionFromTags(item.tags);
      (map[section] = map[section] || []).push(item);
    }
    return map;
  }, [filteredItems]);

  const handleToggle = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Jump-link scrolling
  const scrollToSection = (sectionKey: string) => {
    const el = document.getElementById(sectionKey);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const visibleSections = SECTION_ORDER.filter((s) => (grouped[s] ?? []).length > 0);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO
        title="Frequently Asked Questions"
        description="Answers to common questions about My Investments — FAIS compliance, broker integration, AI trading signals, B2B organisations, and pricing."
        canonical="https://my-investments.io/faq"
      />
      <FaqStructuredData items={filteredItems} />

      {/* ── Header ── */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-3">
            <HelpCircle size={28} className="text-teal-400" />
            <h1 className="text-2xl font-bold text-white">Frequently Asked Questions</h1>
          </div>
          <p className="text-slate-400 text-sm max-w-xl">
            Answers to common questions about my investments — platform features,
            compliance, broker integration, and B2B onboarding.
          </p>

          {/* Search */}
          <div className="mt-5 relative max-w-lg">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search questions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="faq-search"
              className="w-full pl-9 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8 flex gap-8">
        {/* ── Sidebar nav (desktop) ── */}
        <aside className="hidden lg:block w-48 flex-shrink-0">
          <nav className="sticky top-8 space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Jump to
            </p>
            {SECTION_ORDER.map((key) => {
              const count = (grouped[key] ?? []).length;
              if (count === 0 && items.length > 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => scrollToSection(key)}
                  className="block w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:text-white hover:bg-slate-800/50 rounded transition-colors"
                >
                  {SECTION_LABELS[key]}
                  {count > 0 && (
                    <span className="ml-1.5 text-slate-600">({count})</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0">
          {loading && (
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="h-5 bg-slate-800 rounded w-40 mb-3 animate-pulse" />
                  <div className="space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="h-12 bg-slate-800/60 rounded-lg animate-pulse" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-16">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && filteredItems.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-sm">
                {search ? `No results for "${search}". Try a different search term.` : 'No FAQ items found.'}
              </p>
            </div>
          )}

          {!loading && !error && visibleSections.map((sectionKey) => (
            <FaqSection
              key={sectionKey}
              sectionKey={sectionKey}
              items={grouped[sectionKey] ?? []}
              openItems={openItems}
              onToggle={handleToggle}
            />
          ))}

          {/* Items from unknown sections (not in SECTION_ORDER) */}
          {!loading && !error && Object.keys(grouped)
            .filter((k) => !SECTION_ORDER.includes(k as typeof SECTION_ORDER[number]))
            .map((sectionKey) => (
              <FaqSection
                key={sectionKey}
                sectionKey={sectionKey}
                items={grouped[sectionKey]}
                openItems={openItems}
                onToggle={handleToggle}
              />
            ))}
        </main>
      </div>

      {/* ── Footer ── */}
      <div className="border-t border-slate-800 mt-4 py-8 pb-24 lg:pb-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <p className="text-slate-500">
            Can't find what you're looking for?{' '}
            <Link to="/login" className="text-teal-400 hover:text-teal-300">
              Log in
            </Link>{' '}
            and submit a support ticket.
          </p>
          <div className="flex items-center gap-4 text-slate-600">
            <Link to="/welcome" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/mcp" className="hover:text-white transition-colors">
              MCP Integration
            </Link>
            <Link to="/knowledge/glossary" className="hover:text-white transition-colors">
              Glossary
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
